import './rater.retroactive-date.scss';

import { Box, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import { ConvergeDateField, FormCheckBoxField } from '../../../../presentation';
import React, { useMemo } from 'react';
import moment, { Moment } from 'moment';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { displayFormatDateUTC } from '@converge/utils';
import { useRaterStateContextSelector } from '../data';

interface IRaterRetroactiveDateProps {
  uuid: string | null | undefined;
  isDisabled: boolean;
  retroactiveDate: Date | string | null | undefined | Moment;
  fullPriorActs: boolean | null | undefined;
  onRetroactiveDateChange: (newValue: null | Moment) => void;
  onFullPriorActsChange: (newValue: boolean) => void;
}

export const RaterRetroactiveDate = (props: IRaterRetroactiveDateProps) => {
  const policyStartDate = useRaterStateContextSelector(
    ({ submission: { policyStartDate } }) => policyStartDate
  );

  return (
    <UnderwritingRetroactiveDate policyStartDate={policyStartDate} {...props} />
  );
};

export interface IUnderwritingRetroactiveDateProps
  extends IRaterRetroactiveDateProps {
  policyStartDate: Date | string | null | undefined;
  showExpandedView?: boolean;
  retroactiveDateFormName?: string;
}

export const UnderwritingRetroactiveDate = ({
  uuid,
  fullPriorActs,
  isDisabled,
  onFullPriorActsChange,
  onRetroactiveDateChange,
  policyStartDate,
  retroactiveDate,
  retroactiveDateFormName,
  showExpandedView,
}: IUnderwritingRetroactiveDateProps) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );

  const { isSameAsPolicyStartDate, daysDifference } = useMemo(() => {
    const retroactiveMoment = moment.utc(retroactiveDate);
    const policyStartMoment = moment.utc(policyStartDate);
    return {
      isSameAsPolicyStartDate: fullPriorActs
        ? false
        : retroactiveMoment.isSame(policyStartMoment, 'date'),
      daysDifference:
        !retroactiveDate || !fullPriorActs
          ? 0
          : retroactiveMoment.diff(policyStartMoment, 'days'),
    };
  }, [fullPriorActs, policyStartDate, retroactiveDate]);

  return (
    <div>
      <Tooltip
        title={
          <>
            {fullPriorActs
              ? 'Full prior acts'
              : retroactiveDate
              ? `Retroactive date is set to ${displayFormatDateUTC(
                  retroactiveDate
                )}${
                  policyStartDate && isSameAsPolicyStartDate
                    ? ' (policy start date)'
                    : ''
                }`
              : policyStartDate
              ? `Inheriting policy start date of ${displayFormatDateUTC(
                  policyStartDate
                )}`
              : 'No retroactive date set'}
            {typeof daysDifference === 'number' && daysDifference !== 0
              ? ` - ${daysDifference < 0 ? -daysDifference : daysDifference} ${
                  daysDifference === 1 || daysDifference === -1 ? 'day' : 'days'
                } ${daysDifference < 0 ? 'before' : 'after'} policy start`
              : ''}
          </>
        }
      >
        <div
          style={{
            flexDirection: 'row',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          {showExpandedView && (
            <Typography variant="caption">
              {retroactiveDate && !isSameAsPolicyStartDate && !fullPriorActs ? (
                displayFormatDateUTC(retroactiveDate)
              ) : fullPriorActs ? (
                'Full prior acts'
              ) : policyStartDate ? (
                <span style={{ opacity: 0.5 }}>
                  {displayFormatDateUTC(policyStartDate)}
                </span>
              ) : (
                <span>No retroactive date</span>
              )}
            </Typography>
          )}
          <IconButton
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              setAnchorEl(event.currentTarget);
            }}
            color={
              (retroactiveDate && !isSameAsPolicyStartDate) || fullPriorActs
                ? 'primary'
                : undefined
            }
            sx={{
              opacity: isDisabled ? 0.5 : undefined,
            }}
          >
            {fullPriorActs ? (
              <EventAvailableIcon style={{ color: 'black' }} />
            ) : (
              <CalendarMonthIcon />
            )}
          </IconButton>
        </div>
      </Tooltip>
      <Popover
        id={'retroactive-date-popover-' + uuid}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box className="rater-retroactive-date-container">
          <ConvergeDateField
            label={'Retroactive date'}
            placeholder={
              policyStartDate
                ? displayFormatDateUTC(policyStartDate)
                : undefined
            }
            value={!fullPriorActs ? moment.utc(retroactiveDate) : null}
            disabled={!!fullPriorActs}
            onChange={(newValue: Moment | null) => {
              if (moment.isMoment(newValue) && newValue.isValid()) {
                onRetroactiveDateChange(newValue);
              }
            }}
            clearable
            name={retroactiveDateFormName}
          />

          <FormCheckBoxField
            name="fullPriorActs"
            label={'Full prior acts'}
            sx={{ mt: 1 }}
            checked={fullPriorActs === true ? true : false}
            disabled={isDisabled}
            handleChange={(e) => onFullPriorActsChange(e.target.checked)}
          />
        </Box>
      </Popover>
    </div>
  );
};
